import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../Avatar";
import { Badge } from "../Badge";
import { Card } from "../Card";
import { Modal } from "../Modal";
import { StarCounter } from "./StarCounter";
import { DisciplineProgressRing } from "./DisciplineProgressRing";
import { ChildSettingsModal, AbilityDashboard } from "../parent";
import type { DashboardChild } from "../../hooks/useParentDashboard";
import type { DisciplineSlug } from "@jumpbean/shared";

export interface ChildDashboardCardProps {
  child: DashboardChild;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  /** Callback when settings are saved (to trigger refetch) */
  onSettingsSaved?: () => void;
}

// Discipline display order and names
const DISCIPLINE_ORDER: { slug: DisciplineSlug; name: string }[] = [
  { slug: "math", name: "Math" },
  { slug: "spelling", name: "Spelling" },
  { slug: "patterns", name: "Patterns" },
  { slug: "typing", name: "Typing" },
  { slug: "programming", name: "Coding" },
];

/**
 * Child card component for the parent dashboard.
 * Shows child overview with expandable progress details.
 */
export function ChildDashboardCard({
  child,
  isExpanded = false,
  onToggleExpand,
  onSettingsSaved,
}: ChildDashboardCardProps) {
  const navigate = useNavigate();
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAbilityModal, setShowAbilityModal] = useState(false);

  // Format last played time
  function formatLastPlayed(timestamp: string | null): string {
    if (!timestamp) return "Not yet played";

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return "Played just now";
    if (diffHours < 24) return `Played ${diffHours}h ago`;
    if (diffDays === 0) return "Played today";
    if (diffDays === 1) return "Played yesterday";
    if (diffDays < 7) return `Played ${diffDays} days ago`;

    return `Last played ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }

  // Format play time
  function formatPlayTime(seconds: number): string {
    if (seconds < 60) return "< 1 min";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  }

  // Get discipline progress
  function getDisciplineProgress(slug: DisciplineSlug) {
    const discipline = child.progress.disciplines.find(
      (d) => d.disciplineSlug === slug
    );
    return {
      progress: discipline?.percentComplete ?? 0,
      starsEarned: discipline?.totalStarsEarned ?? 0,
      maxStars: discipline?.maxPossibleStars ?? 150,
    };
  }

  return (
    <Card
      variant="elevated"
      className={`
        relative overflow-hidden transition-all duration-300 ease-out
        ${isExpanded ? "ring-2 ring-sunshine-400/50" : ""}
      `}
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <pattern id={`pattern-${child.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="1.5" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill={`url(#pattern-${child.id})`} />
        </svg>
      </div>

      <div className="relative p-5">
        {/* Header Section */}
        <div className="flex items-start gap-4 mb-4">
          {/* Avatar */}
          <div className="relative">
            <Avatar avatarId={child.avatarId} size="lg" name={child.displayName} />
            {/* Streak badge */}
            {child.stats.currentStreakDays > 0 && (
              <div className="absolute -bottom-1 -right-1 bg-gradient-to-br from-prism-red to-prism-red-dark text-white text-xs font-display font-bold rounded-full w-7 h-7 flex items-center justify-center border-2 border-cloud shadow-sm">
                {child.stats.currentStreakDays}🔥
              </div>
            )}
          </div>

          {/* Name and meta */}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-display-sm text-neutral-800 truncate">
              {child.displayName}
            </h3>
            <p className="font-body text-sm text-neutral-500 mt-0.5">
              {formatLastPlayed(child.stats.lastPlayedAt)}
            </p>
          </div>

          {/* Star counter (compact) */}
          <StarCounter count={child.stats.totalStars} size="sm" animate={false} />

          {/* Settings Button */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="
              p-2 rounded-xl
              bg-neutral-100 hover:bg-neutral-200
              text-neutral-500 hover:text-neutral-700
              transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-magic-400
            "
            aria-label={`Learning settings for ${child.displayName}`}
            title="Learning Settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-4 mb-4 p-3 bg-neutral-50 rounded-xl">
          <div className="flex-1 text-center">
            <p className="font-display font-bold text-body-lg text-magic-600">
              {child.stats.totalBadges}
            </p>
            <p className="font-body text-xs text-neutral-500">Badges</p>
          </div>
          <div className="w-px h-8 bg-neutral-200" />
          <div className="flex-1 text-center">
            <p className="font-display font-bold text-body-lg text-meadow-600">
              {child.progress.overallPercentComplete}%
            </p>
            <p className="font-body text-xs text-neutral-500">Complete</p>
          </div>
          <div className="w-px h-8 bg-neutral-200" />
          <div className="flex-1 text-center">
            <p className="font-display font-bold text-body-lg text-sky-600">
              {formatPlayTime(child.stats.totalTimePlayedSeconds)}
            </p>
            <p className="font-body text-xs text-neutral-500">Play Time</p>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center justify-center gap-2 py-2 font-body font-medium text-sm text-magic-500 hover:text-magic-600 transition-colors"
          aria-expanded={isExpanded}
        >
          <span>{isExpanded ? "Show Less" : "View Progress"}</span>
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expanded Content */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-out ${
            isExpanded ? "max-h-[500px] opacity-100 mt-4" : "max-h-0 opacity-0"
          }`}
        >
          {/* Discipline Progress Rings */}
          <div className="mb-5">
            <h4 className="font-display font-semibold text-body-md text-neutral-700 mb-3">
              Progress by Subject
            </h4>
            <div className="flex justify-between items-start overflow-x-auto pb-2 -mx-1 px-1">
              {DISCIPLINE_ORDER.map((discipline) => {
                const progress = getDisciplineProgress(discipline.slug);
                return (
                  <DisciplineProgressRing
                    key={discipline.slug}
                    discipline={discipline.slug}
                    disciplineName={discipline.name}
                    progress={progress.progress}
                    starsEarned={progress.starsEarned}
                    maxStars={progress.maxStars}
                    size="sm"
                  />
                );
              })}
            </div>
          </div>

          {/* Recent Badges */}
          {child.recentBadges.length > 0 && (
            <div className="mb-4">
              <h4 className="font-display font-semibold text-body-md text-neutral-700 mb-3">
                Recent Badges
              </h4>
              <div className="flex items-center gap-3">
                {child.recentBadges.map((badge) => (
                  <div
                    key={badge.id}
                    className="relative"
                    onMouseEnter={() => setHoveredBadge(badge.id)}
                    onMouseLeave={() => setHoveredBadge(null)}
                  >
                    {badge.discipline && (
                      <Badge
                        discipline={badge.discipline.slug}
                        level={badge.level?.number ?? 1}
                        isEarned
                        size="sm"
                      />
                    )}
                    {/* Tooltip */}
                    {hoveredBadge === badge.id && badge.badge && (
                      <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-night text-white text-xs font-body rounded-lg px-2 py-1 whitespace-nowrap z-10 shadow-lg">
                        {badge.badge.name}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-night" />
                      </div>
                    )}
                  </div>
                ))}
                {child.stats.totalBadges > 3 && (
                  <button
                    className="w-12 h-12 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center font-body font-semibold text-sm text-neutral-600 transition-colors"
                    onClick={() => navigate(`/parent/child/${child.id}/badges`)}
                  >
                    +{child.stats.totalBadges - 3}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Streak Info */}
          {child.stats.longestStreakDays > 0 && (
            <div className="p-3 bg-gradient-to-r from-sunshine-50 to-fairy-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🔥</span>
                <div>
                  <p className="font-body font-semibold text-sm text-neutral-700">
                    {child.stats.currentStreakDays > 0
                      ? `${child.stats.currentStreakDays} day streak!`
                      : "Start a new streak today!"}
                  </p>
                  <p className="font-body text-xs text-neutral-500">
                    Best streak: {child.stats.longestStreakDays} days
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            {/* View Abilities Button */}
            <button
              onClick={() => setShowAbilityModal(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-meadow-500 to-meadow-600 hover:from-meadow-600 hover:to-meadow-700 text-cloud rounded-xl font-body font-medium text-sm transition-all shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              View Abilities
            </button>

            {/* View Report Button */}
            <button
              onClick={() => navigate(`/parent/reports/${child.id}`)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-magic-500 to-magic-600 hover:from-magic-600 hover:to-magic-700 text-cloud rounded-xl font-body font-medium text-sm transition-all shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Progress Report
            </button>
          </div>
        </div>
      </div>

      {/* Child Settings Modal */}
      <ChildSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        child={{
          id: child.id,
          displayName: child.displayName,
          avatarId: child.avatarId,
        }}
        onSave={onSettingsSaved}
      />

      {/* Ability Dashboard Modal */}
      <Modal
        isOpen={showAbilityModal}
        onClose={() => setShowAbilityModal(false)}
        title={`${child.displayName}'s Learning Abilities`}
        size="lg"
      >
        <AbilityDashboard
          child={{
            id: child.id,
            displayName: child.displayName,
            avatarId: child.avatarId,
          }}
          onViewFullReport={() => {
            setShowAbilityModal(false);
            navigate(`/parent/reports/${child.id}`);
          }}
        />
      </Modal>
    </Card>
  );
}

export default ChildDashboardCard;
